import PropTypes from 'prop-types';
import React, { useContext } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import BubbleContent from 'components/customer/conversation_history/conversation_items_v2/content/bubble_content';
import ConversationItem from 'models/conversation_item';
import InformationIcon from 'components/lib/icons/information_icon';
import InlineAnnotatedContentContainer from 'components/lib/inline_annotated_content_container';
import ItemContext from 'components/customer/conversation_history/conversation_items_v2/item_context';
import LinkOutStrokeIcon from 'components/common/icons/stroke/link-out-stroke';
import { ModerationStatus } from 'models/conversation_message';
import useConditionalTranslation from 'components/hooks/use_conditional_translation';

export default function FacebookComment({ item, isStacked }) {
  if (isStacked) {
    return <FacebookCommentBubble item={item} />;
  }

  return (
    <div>
      <PostContext item={item} />
      <FacebookCommentBubble item={item} />
    </div>
  );
}

FacebookComment.propTypes = {
  isStacked: PropTypes.bool,
  item: PropTypes.instanceOf(ConversationItem).isRequired,
};

function PostContext({ item }) {
  const { t } = useTranslation();
  const postMessage = item.content.getPostMessage();
  const permalinkUrl = item.content.getPermalinkUrl();
  const adId = item.content.getAdId();

  if (!postMessage && !permalinkUrl && !adId) {
    return null;
  }

  return (
    <PostContextContainer data-aid={`facebookComment-postContext-${item?.id}`}>
      {adId ? <AdBadge>{t('Ad')}</AdBadge> : null}
      {postMessage ? <PostText>{postMessage}</PostText> : <PostTextMissing>{t('Facebook post')}</PostTextMissing>}
      {permalinkUrl ? (
        <PostLink href={permalinkUrl} rel="noopener noreferrer" target="_blank">
          {t('View on Facebook')}
          <LinkOutStrokeIcon />
        </PostLink>
      ) : null}
    </PostContextContainer>
  );
}

PostContext.propTypes = {
  item: PropTypes.instanceOf(ConversationItem).isRequired,
};

function FacebookCommentBubble({ item }) {
  return (
    <BubbleContent
      className="messageItem-content facebookComment"
      data-aid={`messageItem-facebook-comment-${item?.id}`}
      item={item}
    >
      <CommentText item={item} />
      <FacebookModerationStatusIndicator item={item} />
    </BubbleContent>
  );
}

FacebookCommentBubble.propTypes = {
  item: PropTypes.instanceOf(ConversationItem).isRequired,
};

function CommentText({ item }) {
  const { t } = useTranslation();
  const { isViewTranslation } = useContext(ItemContext);
  const text = useConditionalTranslation(item, !isViewTranslation);

  const isEmpty = !text || text.trim() === '';
  const displayText = isEmpty ? emptyCommentText(item, t) : text;

  return (
    <CommentTextWrapper isEmpty={isEmpty}>
      <InlineAnnotatedContentContainer
        hasRedactedPaymentCard={item.content.hasRedactedPaymentCardNumber()}
        id={item.id}
        isRedacted={item.content.isRedacted}
        text={displayText}
      />
    </CommentTextWrapper>
  );
}

CommentText.propTypes = {
  item: PropTypes.instanceOf(ConversationItem).isRequired,
};

function emptyCommentText(item, t) {
  return item.content.getPermalinkUrl()
    ? t('This comment has no text. View it on Facebook to see what was posted.')
    : t('This comment has no text.');
}

export function FacebookModerationStatusIndicator({ item }) {
  const { t } = useTranslation();
  const moderationStatus = item.content.getModerationStatus();

  if (moderationStatus !== ModerationStatus.HIDDEN && moderationStatus !== ModerationStatus.DELETED) {
    return null;
  }

  const message =
    moderationStatus === ModerationStatus.HIDDEN
      ? t("This comment was hidden. The person who posted can still see it, but it's hidden from everyone else.")
      : t('This comment was deleted from Facebook');

  return (
    <ModerationStatusContainer data-aid={`moderation-status-${moderationStatus.toLowerCase()}`}>
      <ModerationStatusMessage>
        <StyledInfoIcon />
        {message}
      </ModerationStatusMessage>
    </ModerationStatusContainer>
  );
}

FacebookModerationStatusIndicator.propTypes = {
  item: PropTypes.instanceOf(ConversationItem).isRequired,
};

const AdBadge = styled.div`
  background: ${p => p.theme.colors.gray200};
  border-radius: 4px;
  color: ${p => p.theme.colors.gray700};
  display: inline-block;
  font-size: var(--font-size-small);
  margin-bottom: 4px;
  padding: 0 6px;
  text-transform: uppercase;
`;

const CommentTextWrapper = styled.div`
  ${p => p.isEmpty && `color: ${p.theme.colors.gray600};`}
`;

const ModerationStatusContainer = styled.div`
  margin-top: 8px;
`;

const ModerationStatusMessage = styled.div`
  color: ${p => p.theme.colors.gray600};
  font-size: var(--font-size-base);
  vertical-align: middle;
`;

const PostContextContainer = styled.div`
  border-left: 4px solid ${p => p.theme.colors.gray300};
  margin-bottom: 8px;
  max-width: 280px;
  padding-left: 12px;
`;

const PostLink = styled.a`
  align-items: center;
  color: ${p => p.theme.colors.green400};
  display: inline-flex;
  font-weight: bold;
  gap: 4px;
  text-decoration: none;

  svg {
    fill: ${p => p.theme.colors.green400};
    height: 12px;
    width: 12px;
  }
`;

const PostText = styled.div`
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  color: ${p => p.theme.colors.gray700};
  display: -webkit-box;
  font-size: var(--font-size-base);
  line-height: 1.4;
  overflow: hidden;
  word-wrap: break-word;
`;

const PostTextMissing = styled(PostText)`
  color: ${p => p.theme.colors.gray600};
  font-style: italic;
`;

const StyledInfoIcon = styled(InformationIcon)`
  display: inline-flex;
  fill: ${p => p.theme.colors.gray600};
  height: 15px;
  margin-right: 5px;
  vertical-align: middle;
`;
